import { StateCreator } from 'zustand'
import { MedplumClient } from '@medplum/core'
import { capitalize } from 'lodash'
import * as Sentry from '@sentry/react'
import { Appointment } from '@medplum/fhirtypes'
import { Visit } from 'components/Patients/Visits/types'
import { GET_CAREGIVER_VISIT } from 'hooks/Encounter/getCaregiverVisit.query'
import { VISIT_QA_TASK_CODES, VisitTaskIdentifiers } from 'fhir/Task/constants'

import { AppointmentActor } from 'components/Schedule/utils/types'
import { getCalculatedVisitStatus } from 'fhir/Encounter/helpers'
import { AppointmentStatus, AppointmentType } from 'fhir/Appointment/constants'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import {
  CalculatedVisitStatus,
  ENCOUNTER_STATUS_CHANGE_REASON_EXTENSION_URL,
  EncounterStatus
} from 'fhir/Encounter/constants'
import { GetCnaVisit_VisitFragment, GetCnaVisitQuery } from 'generated/graphql'
import { captureException, SentrySources } from 'utils/sentry'
import { castToEnum } from 'utils/enum'
import { getErrorInstance } from 'utils/helpers'
import { getLatestEncounterHistoryExtensionValue } from 'components/Visits/helpers'
import { fetchPatientsByIds } from 'services/patient/PatientFetchService'
import {
  fetchAllVisits,
  FetchAllVisitsParams,
  fetchVisitsBySearchParams,
  parseAppointmentInfo,
  ParticipantIdsModel
} from './helpers/visit.helpers'
import { RootSlice } from './rootType'
import { GET_PRACTITIONERS_BY_IDS } from './queries/practitioner.query'

export type VisitSchema = {
  visitType: AppointmentType
  serviceType?: ServiceTypeName
  appointmentId: string
  encounterId?: string
  episodeOfCareId?: string
  taskId?: string
  cnaVisitTaskIds?: string[]
  status: CalculatedVisitStatus
  encounterStatus?: EncounterStatus
  appointmentStatus?: AppointmentStatus
  start: string
  end: string
  reasonForChange?: string
  participantIds: ParticipantIdsModel
  notes: string
}

export type updateVisitsInfoParams = {
  appointmentId: string
  info: {
    status?: CalculatedVisitStatus
    start?: string
    end?: string
    reasonForChange?: string
    notes?: string
    participantIds?: ParticipantIdsModel
  }
}[]

export type LoadVisitsQueryParams = {
  patientId?: string
  startDate?: string
  endDate?: string
  statuses?: AppointmentStatus[]
}

export type VisitAndParams = {
  params: FetchAllVisitsParams
  visits: Visit[]
}

export interface VisitSlice {
  visit: {
    isLoading: boolean
    error: string | null
    list: Record<string, VisitAndParams>
    byId: Record<string, GetCnaVisit_VisitFragment>
    appointmentIdToVisitMapping: Record<string, VisitSchema>
    actorIdToAppointmentIds: {
      [AppointmentActor.PRACTITIONER]: Record<string, string[]>
      [AppointmentActor.PATIENT]: Record<string, string[]>
    }

    loadPaginatedVisits: (
      medplum: MedplumClient,
      appointmentActor: AppointmentActor,
      appointmentActorId: string,
      startDate: string,
      endDate: string,
      statuses: AppointmentStatus[],
      /** When true, dual-fetch GraphQL GET_PATIENTS_BY_IDS and POST /patients/by-ids and compare (patient-data-queries flag). */
      usePatientDataQueries?: boolean
    ) => Promise<void>
    updateVisitsInfo: (params: updateVisitsInfoParams) => void
    deleteVisits: (appointmentIds: string[]) => void
    loadVisit: (medplum: MedplumClient, visitId: string) => Promise<void>
    loadVisits: (medplum: MedplumClient, params: LoadVisitsQueryParams) => Promise<Visit[]>
  }
}

const defaultState: Pick<
  VisitSlice['visit'],
  | 'list'
  | 'byId'
  | 'isLoading'
  | 'error'
  | 'actorIdToAppointmentIds'
  | 'appointmentIdToVisitMapping'
> = {
  list: {},
  byId: {},
  actorIdToAppointmentIds: {
    [AppointmentActor.PRACTITIONER]: {},
    [AppointmentActor.PATIENT]: {}
  },
  appointmentIdToVisitMapping: {},
  isLoading: false,
  error: null
}

export const createVisitSlice: StateCreator<
  RootSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  VisitSlice
> = (set, get) => ({
  visit: {
    ...defaultState,
    updateVisitsInfo: (params: updateVisitsInfoParams) => {
      set((state) => {
        params.forEach(({ appointmentId, info }) => {
          const visit = state.visit.appointmentIdToVisitMapping[appointmentId]
          if (!visit) {
            return
          }
          const { status, start, end, reasonForChange, participantIds, notes } = info
          if (status) {
            visit.status = status
          }
          if (start) {
            visit.start = start
          }
          if (end) {
            visit.end = end
          }
          if (reasonForChange) {
            visit.reasonForChange = reasonForChange
          }
          if (notes) {
            visit.notes = notes
          }
          if (participantIds) {
            visit.participantIds.practitionerIds = participantIds.practitionerIds
          }
        })
      })
    },
    deleteVisits: (appointmentIds: string[]) => {
      set((state) => {
        appointmentIds.forEach((appointmentId) => {
          const appointment = state.visit.appointmentIdToVisitMapping[appointmentId]
          const practitionerParticipantIds = appointment?.participantIds?.practitionerIds ?? []
          practitionerParticipantIds.forEach((id) => {
            const appointmentIdsForPractitioner: string[] =
              state.visit.actorIdToAppointmentIds[AppointmentActor.PRACTITIONER][id] ?? []

            state.visit.actorIdToAppointmentIds[AppointmentActor.PRACTITIONER][id] =
              appointmentIdsForPractitioner.filter((id) => id !== appointmentId)
          })

          const patientParticipantIds = appointment?.participantIds?.patientIds ?? []
          patientParticipantIds.forEach((id) => {
            const appointmentIdsForPatient: string[] =
              state.visit.actorIdToAppointmentIds[AppointmentActor.PATIENT][id] ?? []

            state.visit.actorIdToAppointmentIds[AppointmentActor.PATIENT][id] =
              appointmentIdsForPatient.filter((id) => id !== appointmentId)
          })

          delete state.visit.appointmentIdToVisitMapping[appointmentId]
        })
      })
    },
    loadPaginatedVisits: async (
      medplum,
      appointmentActor,
      appointmentActorId,
      startDate,
      endDate,
      statuses,
      usePatientDataQueries = false
    ): Promise<void> => {
      set((state) => {
        state.visit.error = null
        state.visit.isLoading = true
      })
      try {
        if (!appointmentActor) {
          throw new Error('Invalid role')
        }
        const actorRef = `${capitalize(appointmentActor)}/${appointmentActorId}`
        const appointmentSearchParams = {
          _sort: '-_lastUpdated',
          _count: '1000',
          actor: actorRef,
          _filter: `date ge ${startDate} and date le ${endDate}`,
          status: statuses.join(',')
        }
        // fetch appointments, encounters, visitQaTasks, visitTasks
        const { appointments, encounters, visitQaTasks, visitTasks } =
          await fetchVisitsBySearchParams(medplum, {
            appointment: appointmentSearchParams,
            visitQaTask: {
              code: VISIT_QA_TASK_CODES.join(',')
            },
            visitTask: {
              identifier: VisitTaskIdentifiers.join(',')
            }
          })

        const idToAppointmentMapping = appointments.reduce((acc, appointment) => {
          acc[appointment.id as string] = appointment
          return acc
        }, {} as Record<string, Appointment>)

        const appointmentIds: string[] = appointments.map((appt) => appt.id as string)
        const appointmentIdSet = new Set(appointmentIds)

        let patientIdSet = new Set<string>()
        let practitionerIdSet = new Set<string>()
        // process appointments that have an encounter and assign visit status
        const appointmentMapping = encounters.reduce((apptMappingAcc, encounter) => {
          // if the encounter doesn't have an appointment, skip
          if (!encounter.appointment?.[0].reference) {
            return apptMappingAcc
          }
          const appointmentId = encounter.appointment[0].reference.split('/')[1]
          if (!appointmentIdSet.has(appointmentId)) {
            Sentry.captureException(
              new Error('Appointment not found', {
                cause: {
                  appointment: appointmentId
                }
              })
            )
            return apptMappingAcc
          }
          const currAppointment = idToAppointmentMapping[appointmentId]
          const currEncounterRef = `Encounter/${encounter?.id}`
          const currTask = visitQaTasks.find((task) =>
            Boolean(task?.encounter?.reference === currEncounterRef)
          )
          const encounterVisitTasks = visitTasks.filter((task) =>
            Boolean(task?.encounter?.reference === currEncounterRef)
          )

          const visitStatus = getCalculatedVisitStatus(currAppointment, encounter, currTask)
          if (!visitStatus) {
            throw new Error('Visit status is missing', {
              cause: { appointment: currAppointment, encounter, task: currTask }
            })
          }
          const { visitType, reasonForChange, serviceType, participantIds } =
            parseAppointmentInfo(currAppointment)

          // Assign reasonForChange from encounter status history extension if not already set
          // TODO: See if 'reasonForChange' from parsedAppointmentInfo is correct?
          let updatedReasonForChange = reasonForChange
          if (!updatedReasonForChange) {
            updatedReasonForChange = getLatestEncounterHistoryExtensionValue(
              encounter?.statusHistory ?? [],
              ENCOUNTER_STATUS_CHANGE_REASON_EXTENSION_URL
            )
          }

          patientIdSet = patientIdSet.union(new Set(participantIds.patientIds))
          practitionerIdSet = practitionerIdSet.union(new Set(participantIds.practitionerIds))

          apptMappingAcc[currAppointment.id as string] = {
            visitType,
            serviceType: castToEnum(ServiceTypeName, serviceType),
            appointmentId: currAppointment.id as string,
            encounterId: encounter.id as string,
            episodeOfCareId: encounter.episodeOfCare?.[0]?.reference?.split('/')[1] as string,
            taskId: currTask?.id,
            cnaVisitTaskIds: encounterVisitTasks.map((task) => task.id as string),
            status: visitStatus,
            encounterStatus: encounter.status as EncounterStatus | undefined,
            appointmentStatus: currAppointment.status as AppointmentStatus | undefined,
            start: currAppointment.start as string,
            end: currAppointment.end as string,
            reasonForChange: updatedReasonForChange,
            participantIds,
            notes: currAppointment.comment ?? ''
          }

          return apptMappingAcc
        }, {} as Record<string, VisitSchema>)

        const store = get()
        const patientListData = await fetchPatientsByIds(
          medplum,
          Array.from(patientIdSet),
          usePatientDataQueries
        )
        if (patientListData?.PatientList?.length && patientListData.PatientList.length > 0) {
          store.patient.update(patientListData.PatientList)
        }

        const practitionerResults = await medplum.graphql(GET_PRACTITIONERS_BY_IDS, null, {
          ids: Array.from(practitionerIdSet).join(',')
        })
        if (practitionerResults?.data?.PractitionerList?.length > 0) {
          store.practitioner.update(practitionerResults?.data?.PractitionerList)
        }

        if (appointmentActor) {
          set((state) => {
            // get all appointment ids for the actor
            const appointmentIdSet = state.visit.actorIdToAppointmentIds[appointmentActor][
              appointmentActorId
            ]
              ? new Set([
                  ...state.visit.actorIdToAppointmentIds[appointmentActor][appointmentActorId],
                  ...appointmentIds
                ])
              : new Set(appointmentIds)

            state.visit.actorIdToAppointmentIds[appointmentActor][appointmentActorId] =
              Array.from(appointmentIdSet)

            // add all appointments to the mapping
            state.visit.appointmentIdToVisitMapping = {
              ...state.visit.appointmentIdToVisitMapping,
              ...appointmentMapping
            }
          })
        }
      } catch (error) {
        set((state) => {
          state.visit.error = error?.message || 'Error getting visit data.'
        })
        captureException(getErrorInstance(error), {
          tags: { source: SentrySources.VISITS },
          extras: { params: { appointmentActor, appointmentActorId, startDate, endDate, statuses } }
        })
      } finally {
        set((state) => {
          state.visit.isLoading = false
        })
      }
    },
    loadVisit: async (medplum: MedplumClient, visitId: string): Promise<void> => {
      const store = get()
      if (visitId in store.visit.list) return

      try {
        set((state) => {
          state.visit.isLoading = true
          state.visit.error = null
        })

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: result, errors }: { data: GetCnaVisitQuery; errors: any } =
          await medplum.graphql(GET_CAREGIVER_VISIT, null, { visitId })

        if (errors) {
          throw new Error(JSON.stringify(errors))
        }

        const visit = result?.Encounter

        set((state) => {
          if (visit) {
            state.visit.byId[visitId] = visit
          }
          state.visit.error = null
        })
      } catch (error) {
        set((state) => {
          state.visit.error = error
        })

        throw error
      } finally {
        set((state) => {
          state.visit.isLoading = false
        })
      }
    },
    loadVisits: async (
      medplum: MedplumClient,
      { patientId, startDate, endDate, statuses }: LoadVisitsQueryParams
    ): Promise<Visit[]> => {
      const store = get()

      let visits: Visit[] = []
      const params: FetchAllVisitsParams = {}
      if (patientId) {
        params.patient = `Patient/${patientId}`
      }
      if (startDate && endDate) {
        params._filter = `date ge '${startDate}' and date le '${endDate}'`
      }
      if (statuses) {
        params.status = statuses.join(',')
      }

      try {
        if (
          patientId &&
          patientId in store.visit.list &&
          params._filter === store.visit.list[patientId].params?._filter &&
          params.status === store.visit.list[patientId].params?.status
        ) {
          visits = store.visit.list[patientId].visits
        } else {
          set((state) => {
            state.visit.isLoading = true
            state.visit.error = null
          })

          const visits = await fetchAllVisits(medplum, params)

          set((state) => {
            state.visit.list = {
              ...state.visit.list,
              [patientId ?? '']: {
                params,
                visits
              }
            }
            state.visit.error = null
          })
        }
        return visits
      } catch (error) {
        set((state) => {
          state.visit.error = error
        })
        throw error
      } finally {
        set((state) => {
          state.visit.isLoading = false
        })
      }
    }
  }
})
